import { TitleEscrow__factory } from '../../token-registry-v4/contracts';
import { TitleEscrow__factory as TitleEscrow__factory$1 } from '../../token-registry-v5/contracts';
import { getEthersContractFromProvider } from '../../utils/ethers';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const fetchEscrowTransfersV4 = /* @__PURE__ */ __name(async (provider, address) => {
  const Contract = getEthersContractFromProvider(provider);
  const titleEscrowContract = new Contract(
    address,
    TitleEscrow__factory.abi,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    provider
  );
  const holderChangeLogsDeferred = fetchHolderTransfers(titleEscrowContract);
  const ownerChangeLogsDeferred = fetchOwnerTransfers(titleEscrowContract);
  const [holderChangeLogs, ownerChangeLogs] = await Promise.all([
    holderChangeLogsDeferred,
    ownerChangeLogsDeferred
  ]);
  return [...holderChangeLogs, ...ownerChangeLogs];
}, "fetchEscrowTransfersV4");
const fetchEscrowTransfersV5 = /* @__PURE__ */ __name(async (provider, titleEscrowAddress, tokenRegistryAddress) => {
  const Contract = getEthersContractFromProvider(provider);
  const titleEscrowContract = new Contract(
    titleEscrowAddress,
    TitleEscrow__factory$1.abi,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    provider
  );
  const holderChangeLogsDeferred = await fetchAllTransfers(
    titleEscrowContract,
    titleEscrowAddress,
    tokenRegistryAddress
  );
  return holderChangeLogsDeferred;
}, "fetchEscrowTransfersV5");
const getParsedLogs = /* @__PURE__ */ __name((logs, titleEscrow) => {
  return logs.map((log) => {
    if (!log.blockNumber) throw new Error("Block number not present");
    return {
      ...log,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...titleEscrow.interface.parseLog(log)
    };
  });
}, "getParsedLogs");
const fetchOwnerTransfers = /* @__PURE__ */ __name(async (titleEscrowContract) => {
  const ownerChangeFilter = titleEscrowContract.filters.BeneficiaryTransfer(null, null);
  const ownerChangeLogs = await titleEscrowContract.queryFilter(ownerChangeFilter, 0, "latest");
  const ownerChangeLogsParsed = getParsedLogs(ownerChangeLogs, titleEscrowContract);
  return ownerChangeLogsParsed.map((event) => ({
    type: "TRANSFER_BENEFICIARY",
    owner: event.args.toBeneficiary,
    blockNumber: event.blockNumber,
    transactionHash: event.transactionHash,
    transactionIndex: event.transactionIndex
  }));
}, "fetchOwnerTransfers");
const fetchHolderTransfers = /* @__PURE__ */ __name(async (titleEscrowContract) => {
  const holderChangeFilter = titleEscrowContract.filters.HolderTransfer(null, null);
  const holderChangeLogs = await titleEscrowContract.queryFilter(holderChangeFilter, 0, "latest");
  const holderChangeLogsParsed = getParsedLogs(holderChangeLogs, titleEscrowContract);
  return holderChangeLogsParsed.map((event) => ({
    type: "TRANSFER_HOLDER",
    blockNumber: event.blockNumber,
    holder: event.args.toHolder,
    transactionHash: event.transactionHash,
    transactionIndex: event.transactionIndex
  }));
}, "fetchHolderTransfers");
const fetchAllTransfers = /* @__PURE__ */ __name(async (titleEscrowContract, titleEscrowAddress, tokenRegistryAddress) => {
  const allFilters = [
    titleEscrowContract.filters.HolderTransfer,
    titleEscrowContract.filters.BeneficiaryTransfer,
    titleEscrowContract.filters.TokenReceived,
    titleEscrowContract.filters.ReturnToIssuer,
    // titleEscrowContract.filters.Nomination,
    titleEscrowContract.filters.RejectTransferOwners,
    titleEscrowContract.filters.RejectTransferBeneficiary,
    titleEscrowContract.filters.RejectTransferHolder,
    titleEscrowContract.filters.Shred
  ];
  const allLogs = await Promise.all(
    allFilters.map(async (filter) => {
      const logs = await titleEscrowContract.queryFilter(filter, 0, "latest");
      return logs;
    })
  );
  const holderChangeLogsParsed = getParsedLogs(
    allLogs.flat(),
    titleEscrowContract
  );
  if (!tokenRegistryAddress) {
    tokenRegistryAddress = await titleEscrowContract.registry();
  }
  if (!titleEscrowAddress) {
    titleEscrowAddress = titleEscrowContract?.address ?? await titleEscrowContract.getAddress();
  }
  return holderChangeLogsParsed.map((event) => {
    if (event?.name === "HolderTransfer") {
      return {
        type: "TRANSFER_HOLDER",
        blockNumber: event.blockNumber,
        holder: event.args.toHolder,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "BeneficiaryTransfer") {
      return {
        type: "TRANSFER_BENEFICIARY",
        owner: event.args.toBeneficiary,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "TokenReceived") {
      const type = identifyTokenReceivedType(event);
      return {
        type,
        from: type === "INITIAL" ? "0x0000000000000000000000000000000000000000" : tokenRegistryAddress,
        to: titleEscrowAddress,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "ReturnToIssuer") {
      return {
        type: "RETURNED_TO_ISSUER",
        blockNumber: event.blockNumber,
        // Handle ethers v5 and v6 differently
        from: titleEscrowAddress,
        to: tokenRegistryAddress,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "Nomination") {
      return void 0;
    } else if (event?.name === "RejectTransferOwners") {
      return {
        type: "REJECT_TRANSFER_OWNERS",
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "RejectTransferBeneficiary") {
      return {
        type: "REJECT_TRANSFER_BENEFICIARY",
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "RejectTransferHolder") {
      return {
        type: "REJECT_TRANSFER_HOLDER",
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    } else if (event?.name === "Shred") {
      return {
        type: "RETURN_TO_ISSUER_ACCEPTED",
        blockNumber: event.blockNumber,
        from: tokenRegistryAddress,
        to: "0x00000000000000000000000000000000000dead",
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        remark: event.args?.remark
      };
    }
    return void 0;
  }).filter((event) => event !== void 0);
}, "fetchAllTransfers");
function identifyTokenReceivedType(event) {
  if (event.args.isMinting) {
    return "INITIAL";
  } else {
    return "RETURN_TO_ISSUER_REJECTED";
  }
}
__name(identifyTokenReceivedType, "identifyTokenReceivedType");

export { fetchEscrowTransfersV4, fetchEscrowTransfersV5 };
